import { z } from 'zod';

// ---- Atualizações (Admin › Atualizações, bell, /atualizacoes) ------------------------------------

export const ANNOUNCEMENT_TYPES = ['UPDATE', 'FIX', 'FEATURE'] as const;
export type AnnouncementType = (typeof ANNOUNCEMENT_TYPES)[number];

export const ANNOUNCEMENT_TYPE_LABEL: Record<AnnouncementType, string> = {
  UPDATE: 'Atualização',
  FIX: 'Correção',
  FEATURE: 'Novidade',
};

export const ANNOUNCEMENT_TITLE_MIN = 3;
export const ANNOUNCEMENT_TITLE_MAX = 120;
export const ANNOUNCEMENT_BODY_MAX = 8000;
/** Items per page on /atualizacoes. */
export const ANNOUNCEMENT_PAGE = 20;

/** Trims, drops NUL (Postgres rejects it) and normalizes line breaks; inner blank lines are kept. */
const text = (v: unknown) => (typeof v === 'string' ? v.replace(/\u0000/g, '').replace(/\r\n?/g, '\n').trim() : v);

const title = z.preprocess(
  (v) => (typeof v === 'string' ? (text(v) as string).replace(/\s+/g, ' ') : v),
  z.string().min(ANNOUNCEMENT_TITLE_MIN, `O título precisa de pelo menos ${ANNOUNCEMENT_TITLE_MIN} letras.`).max(ANNOUNCEMENT_TITLE_MAX, `Use até ${ANNOUNCEMENT_TITLE_MAX} caracteres no título.`),
);
const body = z.preprocess(text, z.string().min(1, 'Escreva o que mudou.').max(ANNOUNCEMENT_BODY_MAX, `Use até ${ANNOUNCEMENT_BODY_MAX} caracteres.`));

export const announcementInputSchema = z.object({
  title,
  type: z.enum(ANNOUNCEMENT_TYPES).default('UPDATE'),
  body,
  published: z.boolean().default(false),
});
export type AnnouncementInput = z.input<typeof announcementInputSchema>;

export const announcementPatchSchema = z.object({
  title: title.optional(),
  type: z.enum(ANNOUNCEMENT_TYPES).optional(),
  body: body.optional(),
  published: z.boolean().optional(),
});

export const announcementReadSchema = z.object({ ids: z.array(z.string().min(1).max(40)).min(1).max(50) });

/** What users get: no dates (only the admin sees when it was published). */
export interface AnnouncementDTO {
  id: string;
  type: AnnouncementType;
  title: string;
  body: string;
  /** This user already saw it on /atualizacoes. */
  read: boolean;
}

export interface AnnouncementPageDTO {
  items: AnnouncementDTO[];
  /** Pass as `?cursor=` for the next page; null on the last one. */
  nextCursor: string | null;
}

/** The bell: how many are new for this user, and the newest of them. */
export interface AnnouncementUnreadDTO {
  count: number;
  items: Pick<AnnouncementDTO, 'id' | 'type' | 'title'>[];
}

export interface AdminAnnouncementDTO {
  id: string;
  type: AnnouncementType;
  title: string;
  body: string;
  published: boolean;
  /** First time it went out; kept when it is unpublished and published again. */
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Users who saw it on /atualizacoes. */
  reads: number;
}

export interface AdminAnnouncementsDTO {
  items: AdminAnnouncementDTO[];
  counts: { published: number; drafts: number; total: number };
}
